import { isEmpty } from 'lodash';
import { Link } from 'react-router-dom';
import {
  AppBar, Divider, Grid, IconButton,
  Toolbar, Typography,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { appBar } from '@client/theme/mixins';
import { SideDoorIcon } from '@client/images/logos/SideDoorIcon';
import { useIsMobile } from '@client/hooks/dom.hooks';
import { ImageLoader } from '@client/components/common/Media/common/ImageLoader';
import { getImageLargeUrl } from '@sda/utils/image';
import { getImageArrayFromField } from '@sda/utils/image/getImageArrayFromField';
import { BackButton } from '@client/components/common/BackButton';
import { ToolbarBackButton } from './ToolbarBackButton';
import { Steps } from './Steps';
import type { StepFormWrapperProps } from './interfaces';

const useStyles = makeStyles({ name: 'StepFormWrapper' })((theme) => ({
  root: {
    backgroundColor: theme.palette.background.paper,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    textAlign: 'center',
    height: '100%',
    width: '100%',
    overflow: 'auto',

    '& label': {
      textAlign: 'left',
    },

    [theme.breakpoints.down('md')]: {
      alignItems: 'flex-start',
      position: 'relative',
      minHeight: '100vh',
    },
  },
  appBar: {
    ...appBar(theme),
    position: 'static',
    justifyContent: 'center',

    [theme.breakpoints.up('md')]: {
      position: 'fixed',
    },
  },
  gridContainer: {
    height: '100%',
  },
  explainerContainer: {
    textAlign: 'left',

    [theme.breakpoints.down('md')]: {
      '& > div': {
        padding: theme.spacing(0, 2, 2, 2),
      },
    },

    [theme.breakpoints.up('md')]: {
      display: 'flex',
      alignItems: 'center',

      '& > div': {
        margin: '0 auto',
        padding: theme.spacing(2, 0),
      },
    },
  },
  formContainer: {
    textAlign: 'left',

    [theme.breakpoints.down('md')]: {
      '& > div': {
        padding: theme.spacing(3, 2),
        marginTop: theme.spacing(3),
        borderTopWidth: 1,
        borderTopStyle: 'solid',
        borderTopColor: theme.palette.divider,
      },
    },

    [theme.breakpoints.up('md')]: {
      alignSelf: 'center',
    },
  },
  gridImageContainer: {
    position: 'relative',
    overflow: 'hidden',

    '& > figure > img': {
      position: 'absolute',
    },

    '& > figure > figcaption': {
      position: 'absolute',
      bottom: theme.spacing(1),
      right: theme.spacing(1),
    },

    [theme.breakpoints.up('md')]: {
      display: 'flex',
      justifyContent: 'flex-end',

      '& > figure': {
        position: 'relative',
        height: '100vh',
        aspectRatio: 3 / 4,
      },

      '& > figure > img': {
        height: '100%',
        top: 0,
        left: '50%',
        transform: 'translateX(-50%)',
      },
    },

    [theme.breakpoints.down('md')]: {
      aspectRatio: 16 / 9,
      width: '100%',
      marginBottom: theme.spacing(3),

      '&::before': {
        content: '""',
        display: 'block',
        paddingTop: '56.25%',
      },

      '& > figure > img': {
        width: '100%',
        top: '50%',
        left: 0,
        transform: 'translateY(-50%)',
      },
    },
  },
  gridImageContainerWithToolbar: {
    [theme.breakpoints.up('md')]: {
      '& > figure': {
        height: `calc(100vh - ${theme.spacing(9)})`,
      },
    },
  },
  gridImageContainerFixed: {
    position: 'fixed',
    right: 0,
    top: 0,
    maxWidth: `calc(37.5% - ${theme.spacing(2)})`,
  },
  bottomAppBar: {
    width: '100%',
    height: theme.spacing(8),
    backgroundColor: theme.palette.background.paper,
    borderBottom: 'none',
    borderTopWidth: 1,
    borderTopColor: theme.palette.divider,
    borderTopStyle: 'solid',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    position: 'fixed',
    bottom: 0,
    left: 0,
    top: 'auto',

    [theme.breakpoints.up('md')]: {
      height: theme.spacing(9),
    },

    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(0, 2),
    },
  },
  bottomAppBarSpacer: {
    height: theme.spacing(8),
    minHeight: theme.spacing(8),

    [theme.breakpoints.up('md')]: {
      height: theme.spacing(9),
      minHeight: theme.spacing(9),
    },
  },
  gridDivider: {
    position: 'relative',

    '& > hr': {
      position: 'absolute',
      left: '50%',
      transform: 'translateX(-50%)',
    },
  },
  mobileSteps: {
    margin: theme.spacing(1, 0, 3, 0),
  },
}));

const imageWrapper = {
  id: '1',
  updatedAt: null,
  hash: null,
};

export const StepFormWrapper: React.FC<StepFormWrapperProps> = ({
  displayImageOnMobile = false, displayHeader = true, backButtonUrl, imagePosition = 'static',
  imgSrc, imgTitle, steps, currentStep,
  renderExplainer: Explainer, children: FormComponent, renderToolbar: ToolbarComponent,
}) => {
  const { classes, cx } = useStyles();
  const isMobile = useIsMobile();

  const hasFullGrid = Boolean(Explainer) && Boolean(FormComponent);
  const gridWidth = hasFullGrid ? 5 : 12;

  let imageArray = null;

  if (imgSrc) {
    imageArray = getImageArrayFromField([{ ...imageWrapper, path: imgSrc }], null);
  }

  const hasImage = !isEmpty(imageArray);

  return (
    <div className={classes.root}>
      {Boolean(displayHeader) && (
        <AppBar
          className={classes.appBar}
          color="transparent"
        >
          <Toolbar>
            <IconButton
              size="xlarge"
              edge="start"
              title="Home"
              component={Link}
              to="/home"
            >
              <SideDoorIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
      )}

      {Boolean(isMobile) && Boolean(steps) && (
        <div className={classes.mobileSteps}>
          <Steps
            steps={steps}
            currentStep={currentStep}
          />
        </div>
      )}

      <Grid
        className={classes.gridContainer}
        container
      >
        {!isMobile && (
          <Grid
            item
            xs={1}
          />
        )}

        <Grid
          item
          container
          direction={isMobile ? 'column-reverse' : 'row'}
          alignItems="stretch"
          justifyContent={hasImage && !isMobile ? 'space-between' : 'flex-start'}
          className={classes.gridContainer}
          xs={12}
          md={hasImage ? 11 : 10}
        >
          <Grid
            item
            md={hasImage ? 6.5 : 12}
          >
            <Grid
              container
              direction={isMobile ? 'column' : 'row'}
              alignItems="stretch"
              className={classes.gridContainer}
            >
              {Boolean(Explainer) && (
                <Grid
                  item
                  xs="auto"
                  md={gridWidth}
                  className={classes.explainerContainer}
                >
                  <div>
                    {Explainer}
                  </div>
                </Grid>
              )}

              {!isMobile && Boolean(hasFullGrid) && (
                <Grid
                  item
                  md={2}
                  className={classes.gridDivider}
                >
                  <Divider orientation="vertical" />
                </Grid>
              )}

              {Boolean(FormComponent) && (
                <Grid
                  item
                  xs="auto"
                  md={gridWidth}
                  className={classes.formContainer}
                >
                  <div>
                    {FormComponent}
                  </div>
                </Grid>
              )}
            </Grid>
          </Grid>

          {Boolean(hasImage) && Boolean(!isMobile || displayImageOnMobile) && (
            <Grid
              item
              className={cx(classes.gridImageContainer, Boolean(ToolbarComponent) && classes.gridImageContainerWithToolbar, imagePosition === 'fixed' && classes.gridImageContainerFixed)}
              md={4.5}
            >
              <figure>
                <ImageLoader
                  {...imageArray[0]}
                  sizeCallback={getImageLargeUrl}
                >
                  {(src) => (
                    <img
                      src={src}
                      alt={imgTitle}
                    />
                  )}
                </ImageLoader>

                <Typography
                  component="figcaption"
                  variant="caption"
                  color="white"
                >
                  {imgTitle}
                </Typography>
              </figure>
            </Grid>
          )}
        </Grid>

        {!hasImage && !isMobile && (
          <Grid
            item
            xs={1}
          />
        )}
      </Grid>

      {Boolean(ToolbarComponent) && (
        <>
          <AppBar
            className={classes.bottomAppBar}
            color="transparent"
            component="footer"
          >
            <Grid container>
              {!isMobile && (
                <Grid
                  item
                  xs={1}
                />
              )}

              <Grid
                item
                container
                xs={12}
                md={10}
                justifyContent="space-between"
                alignItems="center"
              >
                <Grid
                  item
                  xs="auto"
                  md={4}
                  justifyContent="flex-start"
                  className="flex"
                >
                  <BackButton
                    to={backButtonUrl}
                    component={ToolbarBackButton}
                  />
                </Grid>

                <Grid item>
                  {!isMobile && Boolean(steps) && (
                    <Steps
                      steps={steps}
                      currentStep={currentStep}
                    />
                  )}
                </Grid>

                <Grid
                  item
                  xs="auto"
                  md={4}
                  justifyContent="flex-end"
                  className="flex"
                >
                  {ToolbarComponent}
                </Grid>
              </Grid>

              {!isMobile && (
                <Grid
                  item
                  xs={1}
                />
              )}
            </Grid>
          </AppBar>

          <div className={classes.bottomAppBarSpacer} />
        </>
      )}
    </div>
  );
};
